import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug, ChevronDown, ChevronUp } from 'lucide-react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
    showDetails?: boolean;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
    showStack: boolean;
}

/**
 * Error Boundary component that catches JavaScript errors anywhere in the child
 * component tree, logs those errors, and displays a fallback UI.
 */
class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            showStack: false,
        };
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        this.setState({ errorInfo });

        // Log error to console
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        // Call optional error handler
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }

        // In production, you might want to send this to an error tracking service
        // e.g., Sentry, LogRocket, etc.
    }

    handleRetry = (): void => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
            showStack: false,
        });
    };

    handleGoHome = (): void => {
        window.location.href = '/';
    };

    handleReload = (): void => {
        window.location.reload();
    };

    toggleStack = (): void => {
        this.setState(prev => ({ showStack: !prev.showStack }));
    };

    render(): ReactNode {
        if (this.state.hasError) {
            // Custom fallback if provided
            if (this.props.fallback) {
                return this.props.fallback;
            }

            const { error, errorInfo, showStack } = this.state;
            const showDetails = this.props.showDetails ?? process.env.NODE_ENV === 'development';

            return (
                <div className="min-h-[400px] flex items-center justify-center p-8">
                    <div className="max-w-lg w-full bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-red-500 to-orange-500 p-6 text-white">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-white/20 rounded-xl">
                                    <AlertTriangle className="w-6 h-6" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold">Something went wrong</h2>
                                    <p className="text-sm text-white/80 mt-1">
                                        We've encountered an unexpected error
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-6 space-y-4">
                            <p className="text-slate-600">
                                Don't worry, your data is safe. You can try refreshing the page or
                                going back to the dashboard.
                            </p>

                            {/* Error message */}
                            {showDetails && error && (
                                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                                    <div className="flex items-start gap-2">
                                        <Bug className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-red-800">
                                                {error.name}: {error.message}
                                            </p>

                                            {/* Stack trace toggle */}
                                            {errorInfo && (
                                                <button
                                                    onClick={this.toggleStack}
                                                    className="flex items-center gap-1 mt-2 text-xs text-red-600 hover:text-red-800"
                                                >
                                                    {showStack ? (
                                                        <>
                                                            <ChevronUp className="w-3 h-3" />
                                                            Hide stack trace
                                                        </>
                                                    ) : (
                                                        <>
                                                            <ChevronDown className="w-3 h-3" />
                                                            Show stack trace
                                                        </>
                                                    )}
                                                </button>
                                            )}

                                            {/* Stack trace */}
                                            {showStack && errorInfo && (
                                                <pre className="mt-2 text-[10px] text-red-700 bg-red-100 rounded-lg p-3 overflow-x-auto max-h-48 overflow-y-auto">
                                                    {errorInfo.componentStack}
                                                </pre>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Action buttons */}
                            <div className="flex flex-col sm:flex-row gap-3 pt-2">
                                <button
                                    onClick={this.handleRetry}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-cobalt-600 hover:bg-cobalt-700 text-white rounded-xl font-medium transition-colors"
                                >
                                    <RefreshCw className="w-4 h-4" />
                                    Try Again
                                </button>
                                <button
                                    onClick={this.handleGoHome}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium transition-colors"
                                >
                                    <Home className="w-4 h-4" />
                                    Go to Dashboard
                                </button>
                            </div>

                            {/* Reload option */}
                            <p className="text-center text-xs text-slate-500">
                                If the problem persists,{' '}
                                <button
                                    onClick={this.handleReload}
                                    className="text-cobalt-600 hover:underline"
                                >
                                    reload the page
                                </button>
                            </p>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

/**
 * Higher-order component to wrap any component with an error boundary
 */
export function withErrorBoundary<P extends object>(
    WrappedComponent: React.ComponentType<P>,
    fallback?: ReactNode
): React.FC<P> {
    return function WithErrorBoundary(props: P) {
        return (
            <ErrorBoundary fallback={fallback}>
                <WrappedComponent {...props} />
            </ErrorBoundary>
        );
    };
}

/**
 * Lightweight error boundary for smaller sections
 */
export const SectionErrorBoundary: React.FC<{ children: ReactNode; name?: string }> = ({
    children,
    name = 'This section',
}) => {
    return (
        <ErrorBoundary
            fallback={
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <div className="flex items-center gap-2 text-amber-800">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="text-sm font-medium">
                            {name} couldn't load properly
                        </span>
                    </div>
                    <p className="text-xs text-amber-600 mt-1">
                        Please try refreshing the page
                    </p>
                </div>
            }
        >
            {children}
        </ErrorBoundary>
    );
};

export default ErrorBoundary;
